import React from 'react';

interface SystemDef {
  id: 'consumer' | 'business';
  label: string;
  tagline: string;
  href: string;
  gradient: string;
  eyebrowColor: string;
  borderHover: string;
  inventory: { label: string; count: string }[];
  figmaUrl: string;
}

const systems: SystemDef[] = [
  {
    id: 'consumer',
    label: 'Consumer',
    tagline: 'Magenta · Dark · Liquid Glass · for people going out',
    href: '/design-systems/consumer/m3-index.html',
    gradient: 'linear-gradient(135deg, #EB2BBC 0%, #A843FB 50%, #06B6D4 100%)',
    eyebrowColor: 'text-fuchsia-300',
    borderHover: 'hover:border-fuchsia-500/50',
    inventory: [
      { label: 'Components', count: '~17 families' },
      { label: 'Variants', count: '~567' },
      { label: 'Pages', count: '8 (Foundations + 7 component pages + Glass)' },
      { label: 'Shape', count: 'Pill (radius 9999)' },
      { label: 'Surface', count: 'Purple-tinted dark · #0A0324' },
    ],
    figmaUrl: 'https://www.figma.com/design/CBJ5J4ZzkY596o3bL485G0/Consumer-Design-System',
  },
  {
    id: 'business',
    label: 'Business',
    tagline: 'Blue · Navy · Gold for revenue · for owners running the venue',
    href: '/design-systems/business/biz-index.html',
    gradient: 'linear-gradient(135deg, #339FFC 0%, #1E3A8A 55%, #FBBF24 100%)',
    eyebrowColor: 'text-blue-300',
    borderHover: 'hover:border-blue-500/50',
    inventory: [
      { label: 'Components', count: '~12 families' },
      { label: 'Variants', count: '~568' },
      { label: 'Pages', count: '10 (Foundations + 7 components + Data Tables + Charts + Dashboards)' },
      { label: 'Shape', count: 'Rounded rect (8px)' },
      { label: 'Surface', count: 'Navy-tinted dark · #0A1424' },
    ],
    figmaUrl: 'https://www.figma.com/design/evhRrFw1jPmA8EhmJmr585/Business-Design-System',
  },
];

function SystemCard({ system }: { system: SystemDef }): React.ReactElement {
  return (
    <a
      href={system.href}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block rounded-2xl bg-gray-900/80 border border-gray-800 ${system.borderHover} overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,0,0,0.5)]`}
    >
      {/* Gradient hero */}
      <div
        className="h-44 relative flex items-end p-6"
        style={{ background: system.gradient }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25), transparent 50%)',
          }}
        />
        <div className="relative">
          <p className={`text-[10px] font-semibold tracking-widest uppercase mb-1 text-white/80`}>
            {system.id === 'consumer' ? 'SPARK CONSUMER' : 'OWNER BUSINESS'}
          </p>
          <h3 className="font-display text-3xl font-extrabold text-white tracking-tight leading-none">
            {system.label}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        <p className="text-sm text-gray-400 mb-5 leading-relaxed">{system.tagline}</p>

        <dl className="space-y-2 mb-5">
          {system.inventory.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-4 text-sm">
              <dt className="text-gray-500 shrink-0">{row.label}</dt>
              <dd className="text-gray-200 text-right">{row.count}</dd>
            </div>
          ))}
        </dl>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <span className={`text-[11px] font-semibold tracking-wider uppercase ${system.eyebrowColor}`}>
            Open system →
          </span>
          <span className="text-[11px] text-gray-500">opens in new tab</span>
        </div>
      </div>
    </a>
  );
}

function Designs(): React.ReactElement {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-gray-800">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              'radial-gradient(circle at 20% 20%, rgba(235,43,188,0.5), transparent 50%), radial-gradient(circle at 80% 70%, rgba(51,159,252,0.4), transparent 50%)',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 py-12">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-2">
            SocialSpark · Design Systems
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight">
            Designs
          </h1>
          <p className="text-base text-gray-300 max-w-2xl leading-relaxed">
            The two M3 design systems powering SocialSpark. Consumer for the going-out audience.
            Business for owners running the venue. Sister systems — same DNA, opposite temperature,
            Spark gradient as the cross-brand signature.
          </p>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {systems.map((s) => (
            <SystemCard key={s.id} system={s} />
          ))}
        </div>

        {/* Figma links footer */}
        <div className="mt-10 rounded-2xl bg-gray-900/60 border border-gray-800 p-6">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-3">
            Source of truth · Figma
          </p>
          <p className="text-sm text-gray-400 mb-4">
            The HTML systems above are the prototypes. The Figma libraries are the editable source
            with full component variants, color/typography variables, and effect styles.
          </p>
          <div className="flex flex-wrap gap-3">
            {systems.map((s) => (
              <a
                key={s.id}
                href={s.figmaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700 hover:text-white transition-all"
              >
                {s.label} Figma ↗
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Designs;
